import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { EntityApiService } from "../../../../shared/store/entity-store/entity-api.service";
import { environment } from "../../../../../environments/environment";
import { OF } from "../../../of/models/of.model";
import { OfEtat } from "../../../of/enums/etat-of.enum";
import { OFCoutArbreNode } from "../../../of/models/cout-arbre.model";
import { queryParamToHttp } from "../../../../shared/store/utils/store-utils.service";
import { map } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class OfApiService implements EntityApiService<OF> {
  loadOne(id: string): Observable<OF> {
    return this.httpClient.get<OF>(`${environment.api()}/of/id/eager/${id}`);
  }

  httpClient = inject(HttpClient);

  create(entity: OF, query: { auto_plan: string }): Observable<OF> {
    let params = queryParamToHttp(query);
    return this.httpClient.post<OF>(`${environment.api()}/of`, entity, {
      params,
    });
  }

  remove(id: string): Observable<string> {
    return this.httpClient
      .delete(`${environment.api()}/of/${id}`)
      .pipe(map((o) => id));
  }

  update(entity: OF): Observable<OF> {
    return this.httpClient.post<OF>(`${environment.api()}/of`, entity);
  }

  loadAll(): Observable<OF[]> {
    return this.httpClient.get<OF[]>(`${environment.api()}/of/nav`);
  }

  loadCoutArbre(ofId: string): Observable<OFCoutArbreNode> {
    return this.httpClient.get<OFCoutArbreNode>(
      `${environment.api()}/of/id/${ofId}/cout_arbre`,
    );
  }

  updateQuantite(ofId: string, quantite: number): Observable<OF> {
    return this.httpClient.post<OF>(
      `${environment.api()}/of/update_quantite/${ofId}`,
      { quantite },
    );
  }

  annuler(ofId: string): Observable<OF> {
    return this.httpClient.post<OF>(
      `${environment.api()}/of/cancel/${ofId}`,
      null,
    );
  }

  planify(ofId: string): Observable<OF> {
    return this.httpClient.post<OF>(
      `${environment.api()}/of/planify/${ofId}`,
      null,
    );
  }

  loadForSelection(ligneId?: string): Observable<OF[]> {
    let params = new HttpParams();
    if (ligneId) {
      params = params.append('ligneId', ligneId);
    }
    return this.httpClient.get<OF[]>(
      `${environment.api()}/of/for_selection`,
      { params },
    );
  }

  loadAllFilter(filters: {
    dluoDates: { start: Date; end: Date };
    ofDates: { start: Date; end: Date };
    etats: OfEtat[];
    lignes: string[];
    search: string;
    sort: string;
    articleId: string;
    page?: number;
  }) {
    let params = new HttpParams();

    if (filters.dluoDates.start) {
      params = params.set("minDateDluo", filters.dluoDates.start.toISOString());
    }
    if (filters.sort) {
      params = params.set("sort", filters.sort);
    }
    if (filters.page != null) {
      params = params.set("page", String(filters.page));
    }
    if (filters.articleId) {
      params = params.set("articleId", filters.articleId);
    }
    if (filters.dluoDates.end) {
      params = params.set("maxDateDluo", filters.dluoDates.end.toISOString());
    }
    if (filters.ofDates.start) {
      params = params.set("minDate", filters.ofDates.start.toISOString());
    }
    if (filters.ofDates.end) {
      params = params.set("maxDate", filters.ofDates.end.toISOString());
    }
    if (filters.etats && filters.etats.length > 0) {
      filters.etats.forEach((etat) => {
        params = params.append("etats", etat.toString());
      });
    }
    if (filters.lignes && filters.lignes.length > 0) {
      filters.lignes.forEach((ligne) => {
        params = params.append("ligneId", ligne);
      });
    }
    if (filters.search) {
      params = params.set("search", filters.search);
    }

    return this.httpClient.get<OF[]>(`${environment.api()}/of/navfilter`, {
      params,
    });
  }
}
